import { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import * as Icons from "lucide-react"
import { getWorkflowStatisticsp2p } from "../../../../ApiCalls/mantisFlowsManangment"

// Caché global para estadísticas
const statsCache = new Map();
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutos

function ControlPanelStats({ mantisBusiness }) {
  const [stats, setStats] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [cacheHit, setCacheHit] = useState(false)

  const getIcon = (iconName) => {
    const iconNamePascal = iconName.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')
    
    const Icon = Icons[iconNamePascal] || Icons.BarChart4
    return <Icon className="h-5 w-5" />
  }

  const getIconColor = (color, name) => {
    if (color) {
      switch (color.toLowerCase()) {
        case 'blue': return 'text-blue-500';
        case 'green': return 'text-emerald-500';
        case 'red': return 'text-red-500';
        case 'yellow': return 'text-amber-500';
        case 'purple': return 'text-purple-500';
        case 'orange': return 'text-orange-500';
        case 'teal': return 'text-teal-500';
        case 'indigo': return 'text-indigo-500';
        case 'pink': return 'text-pink-500';
        default: return 'text-gray-500';
      }
    }
    
    const nameLower = name.toLowerCase()
    if (nameLower.includes('total')) return 'text-emerald-500'
    if (nameLower.includes('validado')) return 'text-blue-500'
    if (nameLower.includes('pendiente')) return 'text-amber-500'
    if (nameLower.includes('rechazado')) return 'text-red-500'
    return 'text-gray-500'
  }

  const formatStatValue = (value) => {
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
      try {
        const num = Number(value);
        return num.toLocaleString('es-CO');
      } catch (e) {
        return value;
      }
    }
    return value;
  };

  const loadStats = useCallback(async (forceRefresh = false) => {
    try {
      const clientId = mantisBusiness._id;
      const cacheKey = `control_panel_${clientId}`;
      const now = Date.now();
      
      if (!forceRefresh && statsCache.has(cacheKey) && (now - statsCache.get(cacheKey).timestamp < CACHE_EXPIRY_TIME)) {
        setStats(statsCache.get(cacheKey).data);
        setIsLoading(false);
        setCacheHit(true);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      setCacheHit(false);
      
      const response = await getWorkflowStatisticsp2p(clientId);
      console.log('Response from API:', response);
      
      if (response.success && response.data && response.data[0]?.data && Array.isArray(response.data[0].data)) {
        console.log('Processing stats data:', response.data[0].data);
        // Usar directamente los datos del backend
        const formattedStats = response.data[0].data.map(stat => ({
          id: stat.id,
          name: stat.name,
          icon: stat.icon.toLowerCase(),
          color: stat.color,
          value: stat.value
        }));
        
        console.log('Formatted stats:', formattedStats);
        
        statsCache.set(cacheKey, {
          data: formattedStats,
          timestamp: now
        });
        
        setStats(formattedStats);
      } else {
        console.log('No valid data found in response:', response);
        if (response.message === "No hay estadísticas disponibles") {
          setError("No hay estadísticas disponibles");
        } else {
          setError("Error al cargar las estadísticas");
        }
        setStats([]);
      }
    } catch (err) {
      console.error("Error al cargar estadísticas:", err);
      setError(err.message || "Error al cargar las estadísticas");
      setStats([]);
    } finally {
      setIsLoading(false);
    }
  }, [mantisBusiness._id]);

  useEffect(() => {
    loadStats(false);
  }, [loadStats]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      loadStats(true);
    }
  }, [refreshTrigger, loadStats]);

  const refreshStats = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const StatItem = ({ name, value, icon, color }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        flex flex-col items-center justify-center gap-2 px-2 py-4
        transition-all duration-300
        hover:bg-slate-50 dark:hover:bg-slate-700/50
      `}
    >
      <div className={`
        flex items-center justify-center w-10 h-10 rounded
        bg-gradient-to-br from-slate-50 to-slate-100
        dark:from-slate-800 dark:to-slate-900
        shadow-sm border border-slate-200 dark:border-slate-700 mb-2
      `}>
        <span className={`${getIconColor(color, name)}`}>
          {getIcon(icon)}
        </span>
      </div>
      <div className="flex-1 min-w-0 text-center">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
          {name}
        </p>
        <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
          {formatStatValue(value)}
        </p>
      </div>
    </motion.div>
  );

  const EmptyState = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icons.BarChart4 className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        {error === "No hay estadísticas disponibles" 
          ? "No hay estadísticas disponibles"
          : "Error al cargar estadísticas"}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        {error === "No hay estadísticas disponibles"
          ? "No se encontraron datos estadísticos para mostrar. Intenta actualizar más tarde."
          : "Hubo un problema al cargar las estadísticas. Por favor, intenta nuevamente."}
      </p>
      <button
        onClick={refreshStats}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
      >
        <Icons.RefreshCw className="w-4 h-4" />
        Actualizar
      </button>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-8"
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <Icons.BarChart3 className="w-5 h-5 text-slate-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Estadísticas del Panel de Control
            </h3>
          </div>
          <button 
            onClick={refreshStats}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 bg-slate-100 dark:bg-slate-700 rounded transition-colors"
            title="Actualizar estadísticas"
          >
            <Icons.RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{cacheHit ? "Actualizar" : "Recargar"}</span>
          </button>
        </div>
        
        {isLoading ? (
          <div className="overflow-x-auto">
            <div className="min-w-[900px] grid grid-cols-5 divide-x divide-slate-200 dark:divide-slate-700">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex flex-col items-center justify-center gap-2 px-2 py-4">
                  <div className="w-10 h-10 rounded bg-slate-200 dark:bg-slate-700 mb-2" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-1" />
                  <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-12" />
                </div>
              ))}
            </div>
          </div>
        ) : stats.length > 0 ? (
          <div className="overflow-x-auto">
            <div className="min-w-[900px] grid grid-cols-5 divide-x divide-slate-200 dark:divide-slate-700">
              {stats.map((stat) => (
                <StatItem 
                  key={stat.id}
                  name={stat.name}
                  value={stat.value}
                  icon={stat.icon}
                  color={stat.color}
                />
              ))}
            </div>
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </motion.div>
  );
}

export default ControlPanelStats; 